//@ts-ignore — package exports not resolved with moduleResolution: node
import * as changeKeys from 'change-case/keys'
import { http } from 'src/services/search/http'

export enum SearchType {
  STORAGE_OBJECT = 'storage_object',
  CAMPAIGN = 'campaign',
  CALL = 'call',
  PHONE_NUMBER = 'phone_number',
  FORM = 'form',
  USER = 'user',
  USER_GROUP = 'user_group',
  TEMPLATE = 'template',
}

export enum ServiceType {
  AUTH = 'auth',
  MARKETING = 'marketing',
  COMMUNICATIONS = 'communications',
  FORMS = 'forms',
}

export interface IGetAggregatedSearchObjectProps {
  search: string
  types: SearchType[]
  perPage?: number
  cursor?: string
}

export interface ISearchSectionResult<T> {
  type: SearchType
  hits: T[]
  nextCursor: string | null
  hasMore: boolean
  totalCount: number
}

export interface IAggregatedSearchResult<T = unknown> {
  query: string
  sections: ISearchSectionResult<T>[]
  skipped: { section: SearchType; reason: string }[]
  errors: {
    section: SearchType
    error: { code: string; retryable: boolean }
  }[]
}

/** @deprecated Use IAggregatedSearchResult */
export type IGetAggreagatedSearchObjectResponse<T> = IAggregatedSearchResult<T>

type MetaFrame = {
  type: 'meta'
  protocol: number
  query: string
  sections: SearchType[]
  skipped?: { section: SearchType; reason: string }[]
}

type SectionFrame<T> = {
  type: 'section'
  section: SearchType
  hits: T[]
  nextCursor: string | null
  hasMore: boolean
  totalCount: number
}

type SectionSkippedFrame = {
  type: 'section_skipped'
  section: SearchType
  reason: string
}

type SectionErrorFrame = {
  type: 'section_error'
  section: SearchType
  error: { code: string; retryable: boolean }
}

type DoneFrame = {
  type: 'done'
  sectionsOk: number
  sectionsError: number
  durationMs: number
}

type SearchStreamFrame<T> =
  | MetaFrame
  | SectionFrame<T>
  | SectionSkippedFrame
  | SectionErrorFrame
  | DoneFrame

const parseSseDataFrames = (raw: string): unknown[] => {
  const frames: unknown[] = []

  for (const rawEvent of raw.split('\n\n')) {
    const dataLines = rawEvent
      .split('\n')
      .filter((line) => line.startsWith('data:'))
      .map((line) => line.slice(5).trimStart())

    if (dataLines.length > 0) {
      frames.push(JSON.parse(dataLines.join('\n')))
    }
  }

  return frames
}

const serializeSearchParams = (params: Record<string, unknown>) => {
  const searchParams = new URLSearchParams()

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return

    if (key === 'types' && Array.isArray(value)) {
      value.forEach((type) => searchParams.append('types[]', String(type)))

      return
    }

    searchParams.append(key, String(value))
  })

  return searchParams.toString()
}

/**
 * Consumes SSE `/search/stream` until the connection closes and returns aggregated sections.
 * Frames: meta → section | section_skipped | section_error → done.
 */

export interface IGetSearchObjectsProps {
  search: string
  types: SearchType[]
  perPage?: number
  cursor?: string
}

export interface SectionResult<T> {
  type: SearchType
  hits: T[]
  nextCursor: string | null
  hasMore: boolean
  totalCount: number
}

export interface IGetSearchObjectResult<T> {
  service: ServiceType
  sections: SectionResult<T>[]
}

export const getSearchObjects = <T = unknown>({
  perPage = 20,
  ...params
}: IGetSearchObjectsProps): Promise<IGetSearchObjectResult<T>[]> => {
  return http
    .get<{ results: IGetSearchObjectResult<T>[] }>('/search', {
      params: { ...params, perPage },
    })
    .then((response) => response.data.results)
}

export const getAggregatedSearchObject = async <T = unknown>({
  perPage = 20,
  ...params
}: IGetAggregatedSearchObjectProps): Promise<IAggregatedSearchResult<T>> => {
  const response = await http.get<string>('/search/stream', {
    params: { ...params, perPage },
    paramsSerializer: serializeSearchParams,
    headers: { Accept: 'text/event-stream' },
    responseType: 'text',
    transformResponse: [(data) => data],
  })

  const sections: ISearchSectionResult<T>[] = []
  const skipped: IAggregatedSearchResult<T>['skipped'] = []
  const errors: IAggregatedSearchResult<T>['errors'] = []
  let query = params.search

  for (const rawFrame of parseSseDataFrames(response.data)) {
    const frame = changeKeys.camelCase(rawFrame as object, 20, {
      mergeAmbiguousCharacters: false,
    }) as SearchStreamFrame<T>

    switch (frame.type) {
      case 'meta':
        query = frame.query
        if (frame.skipped?.length) {
          skipped.push(...frame.skipped)
        }
        break
      case 'section':
        sections.push({
          type: frame.section,
          hits: frame.hits ?? [],
          nextCursor: frame.nextCursor ?? null,
          hasMore: Boolean(frame.hasMore),
          totalCount: frame.totalCount ?? 0,
        })
        break
      case 'section_skipped':
        skipped.push({ section: frame.section, reason: frame.reason })
        break
      case 'section_error':
        errors.push({ section: frame.section, error: frame.error })
        break
      case 'done':
        return { query, sections, skipped, errors }
    }
  }

  return { query, sections, skipped, errors }
}

export const findSearchSection = <T>(
  result: IAggregatedSearchResult<T>,
  type: SearchType
) => result.sections.find((section) => section.type === type)
